<template>
  <div class="abhero">
    <div class="abhero__rows">
      <div
        v-for="(row, idx) in localRows"
        :key="row.key"
        class="abhero__rowwrap"
      >
        <div class="abhero__row">
          <div class="abhero__label">
            {{ row.label }}
          </div>
          <div
            class="abhero__input"
            :class="{ 'abhero__input--disabled': disabled, 'abhero__input--original': isOriginal(row) }"
          >
            <!-- "Use original" selected: this variant uses the SOURCE image as-is, with
                 no AI applied. Show the source thumbnail + label + a clear (×) that
                 reverts the row to the normal AI editbox. (POR-13544) -->
            <template v-if="isOriginal(row)">
              <img
                v-if="sourceThumb && !sourceThumbError"
                :src="sourceThumb"
                :data-fullsrc="sourceFull"
                class="abhero__thumb abhero__thumb--dashed"
                alt=""
                @error="sourceThumbError = true"
              >
              <i
                v-else
                class="bi bi-image abhero__thumb abhero__thumb--fallback"
                title="Original source image"
              />
              <span class="abhero__originaltext">Original source image</span>
              <button
                type="button"
                class="abhero__clear"
                :disabled="disabled"
                title="Use AI instead"
                @click="clearOriginal(idx)"
              >
                &times;
              </button>
            </template>
            <template v-else>
              <img
                v-if="row.image && thumbSrc(row) && !thumbErrors[row.key]"
                :src="thumbSrc(row)"
                :data-fullsrc="fullSrc(row)"
                class="abhero__thumb"
                alt=""
                @error="$set(thumbErrors, row.key, true)"
              >
              <i
                v-else-if="row.image && thumbErrors[row.key]"
                class="bi bi-image abhero__thumb abhero__thumb--fallback"
                title="Selected image (preview unavailable)"
              />
              <textarea
                v-model="row.text"
                rows="1"
                class="abhero__text"
                :placeholder="placeholderFor(row)"
                :disabled="disabled"
                @paste="onPaste(idx, $event)"
                @input="onText($event)"
              />
              <!-- Icon order mirrors the chat composer's bottom bar: clear (×), upload (Paperclip),
                   browse (Folder), save/reuse prompt (Sparkles), and "Use original" (FileImage). The
                   clear × lives INSIDE the icon group (POR-13563-2) so it sits tight against the others
                   instead of being pushed away by the input's flex gap. Upload/browse/sparkles/use-original
                   are Lucide glyphs inlined as SVG to match the source (Lovable/shadcn ship lucide-react)
                   design exactly — Bootstrap glyphs are a different set and never lined up. The narrower
                   FileImage gets a 1px optical nudge (--useorig) so the gaps stay even. They inherit the
                   button colour via stroke="currentColor". (POR-13385-7) -->
              <div class="abhero__icons">
                <button
                  v-if="hasText(row) || row.image"
                  type="button"
                  class="abhero__clear"
                  :disabled="disabled"
                  title="Clear"
                  @click="clearContent(idx)"
                >
                  &times;
                </button>
                <button
                  type="button"
                  class="abhero__icon"
                  :disabled="disabled"
                  title="Upload from device"
                  @click="pick(idx, 'upload')"
                >
                  <svg
                    class="abhero__icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m16 6-8.414 8.586a2 2 0 0 0 2.829 2.829l8.414-8.586a4 4 0 1 0-5.657-5.657l-8.379 8.551a6 6 0 1 0 8.485 8.485l8.379-8.551" />
                  </svg>
                </button>
                <button
                  type="button"
                  class="abhero__icon"
                  :disabled="disabled"
                  title="Pick from file browser or Salsify"
                  @click="pick(idx, 'browser')"
                >
                  <svg
                    class="abhero__icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m6 14 1.5-2.9A2 2 0 0 1 9.24 10H20a2 2 0 0 1 1.94 2.5l-1.54 6a2 2 0 0 1-1.95 1.5H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3.9a2 2 0 0 1 1.69.9l.81 1.2a2 2 0 0 0 1.67.9H18a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
                <!-- Save or reuse a prompt via the Clipboard — variants only. Opens the Clipboard
                     sidebar bound to THIS editbox (Prompt tab pre-selected). (POR-13563-2) -->
                <button
                  v-if="row.key !== 'source'"
                  type="button"
                  class="abhero__icon"
                  :disabled="disabled"
                  title="Save or reuse a prompt"
                  @click="promptClipboard(idx)"
                >
                  <svg
                    class="abhero__icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .962 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.962 0z" />
                    <path d="M20 3v4" />
                    <path d="M22 5h-4" />
                    <path d="M4 17v2" />
                    <path d="M5 18H3" />
                  </svg>
                </button>
                <!-- "Use original" — variants only; the Source row IS the original. (POR-13544) -->
                <button
                  v-if="row.key !== 'source'"
                  type="button"
                  class="abhero__icon abhero__icon--useorig"
                  :disabled="disabled"
                  title="Use original"
                  @click="useOriginal(idx)"
                >
                  <svg
                    class="abhero__icon-svg"
                    xmlns="http://www.w3.org/2000/svg"
                    width="15"
                    height="15"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M6 22a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h8a2.4 2.4 0 0 1 1.704.706l3.588 3.588A2.4 2.4 0 0 1 20 8v12a2 2 0 0 1-2 2z" />
                    <path d="M14 2v5a1 1 0 0 0 1 1h5" />
                    <circle
                      cx="10"
                      cy="12"
                      r="2"
                    />
                    <path d="m20 17-1.296-1.296a2.41 2.41 0 0 0-3.408 0L9 22" />
                  </svg>
                </button>
              </div>
            </template>
          </div>
        </div>
        <div
          v-if="row.key !== 'source' && !disabled && !isOriginal(row) && !hasText(row) && samplesFor(row).length"
          class="abhero__samplewrap"
        >
          <button
            v-for="(ex, exIdx) in samplesFor(row)"
            :key="exIdx"
            type="button"
            class="abhero__sample"
            :class="{ 'abhero__sample--clamp': ex.clamp }"
            :disabled="disabled"
            :title="ex.text"
            @click="applyChip(idx, ex.text)"
          >
            {{ ex.label }}
          </button>
        </div>
      </div>
    </div>
    <div class="abhero__footer">
      <label class="abhero__size">
        <span class="abhero__size-label">Image size</span>
        <select
          v-model="localSize"
          class="abhero__size-select"
          :disabled="disabled"
          title="Output size — PDP images are square (1:1)"
          @change="emitState"
        >
          <option
            v-for="opt in sizeOptions"
            :key="opt"
            :value="opt"
          >
            {{ opt.replace('x', ' × ') }}
          </option>
        </select>
      </label>
      <button
        type="button"
        class="abhero__generate"
        :disabled="!canGenerate || disabled"
        @click="generate"
      >
        <span v-if="!generating">Generate <i class="bi bi-arrow-right" /></span>
        <span v-else>Generating <span class="abhero__dots">···</span></span>
      </button>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AIChatWidgetAbHeroPicker',
  props: {
    rows: { type: Array, required: true },
    generating: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    // Output size, "WxH". PDP images are square (1:1); default 2000x2000. (POR-13385)
    size: { type: String, default: '2000x2000' },
    // Parent writes back a resolved image here: { rowKey, url, thumbnail, name }
    imageResult: { type: Object, default: null },
    // Index of the chat message this form belongs to, so a Clipboard prompt inserted
    // from the sidebar can be routed back to the correct form/row. (POR-13563-2)
    messageIndex: { type: Number, default: null },
  },
  data() {
    return {
      localRows: (this.rows || []).map((r) => ({ ...r, image: r.image || null })),
      localSize: this.size || '2000x2000',
      sizeOptions: ['1500x1500', '2000x2000', '2500x2500', '3000x3000'],
      // Example prompts per variant — clicking one fills that row's editbox with its full
      // text. Shown only while the row is empty. A plain string is displayed verbatim; an
      // { label, text } object displays the short label (clamped to 2 lines) but inserts the
      // full text — used for long examples we don't want to show in full. Variant B offers a
      // second, richer example (style reference) as an alternative to choose from. (POR-13564)
      sampleTexts: {
        variantA: [
          'Remove unneeded text from the product image. Make the "64 oz" text much larger, but keep it on the product image. Keep entire product image visible.',
        ],
        variantB: [
          'Using the provided product image, add a rectangular vertical callout to the right of the image showing "ORANGE" and "64 OZ" in it suitable for a mobile hero image on a retailer image carousel web site.',
          {
            label: 'Remove unneeded text… Make "80 drops" larger. Use a style reference to add lemon wedges and honeycomb behind the product.',
            text: 'Remove unneeded text from the product image. Make the "80 drops" text much larger, but keep it on the product image. Keep entire product image visible. Using the provided image as a style reference, add lemon wedges behind the product to the left, and honeycomb behind the product to the right. Maintain a white background.',
          },
        ],
      },
      // rowKey -> true when its thumbnail <img> failed to load (show a fallback icon)
      thumbErrors: {},
      // The shared "Original source image" thumbnail failed to load → fallback icon. (POR-13544)
      sourceThumbError: false,
    };
  },
  computed: {
    canGenerate() {
      // A variant set to "Use original" needs no text/image of its own — it reuses the
      // source image as-is. (POR-13544)
      return this.localRows.every((r) => r.mode === 'original' || (r.text && r.text.trim().length > 0) || r.image);
    },
    // The source row's thumbnail, shown on every variant that picked "Use original". (POR-13544)
    sourceThumb() {
      const src = (this.localRows || []).find((r) => r.key === 'source');
      return src && src.image ? this.thumbSrc(src) : '';
    },
    // Full-res original behind the source thumbnail, for the click-to-zoom lightbox. (POR-13385-7)
    sourceFull() {
      const src = (this.localRows || []).find((r) => r.key === 'source');
      return src && src.image ? this.fullSrc(src) : '';
    },
  },
  watch: {
    imageResult(val) {
      if (!val || !val.rowKey) return;
      const i = this.localRows.findIndex((r) => r.key === val.rowKey);
      if (i === -1) return;
      this.$set(this.thumbErrors, val.rowKey, false); // new image → re-attempt the thumbnail
      if (val.rowKey === 'source') this.sourceThumbError = false; // re-attempt the "Use original" thumb too
      this.$set(this.localRows, i, {
        ...this.localRows[i],
        image: { url: val.url, thumbnail: val.thumbnail || val.url, name: val.name || '' },
      });
      this.emitState();
    },
  },
  mounted() {
    // Size any pre-filled textareas (e.g. multi-line prompts restored on reload)
    // to fit their content so nothing is clipped. Grow after nextTick AND after the
    // next paint: on a fresh mount/restore the input width isn't final on the first
    // tick, so a single measure can land short and leave a scrollbar (the Variant A
    // bug). The rAF re-measure once layout has settled removes it. (POR-13385-3)
    this.$nextTick(() => this.growAll());
    // Receive a Clipboard prompt the user picked in the sidebar after opening it from a
    // variant's sparkle icon; the parent broadcasts it and only the matching form/row
    // applies it. (POR-13563-2)
    this.$root.$on('abhero:insert-prompt', this.onInsertPrompt);
  },
  beforeDestroy() {
    this.$root.$off('abhero:insert-prompt', this.onInsertPrompt);
  },
  methods: {
    placeholderFor(row) {
      return row.key === 'source'
        ? 'Source product hero — ASIN, paste, or upload'
        : 'ASIN, paste image, or describe the variation…';
    },
    hasText(row) {
      return !!(row && row.text && row.text.trim());
    },
    // Normalised list of example prompts for a variant row. A plain string shows verbatim;
    // an { label, text } object shows the short label (clamped to 2 lines) but inserts the
    // full text. (POR-13564)
    samplesFor(row) {
      const list = (row && this.sampleTexts[row.key]) || [];
      return list.map((ex) => (typeof ex === 'string'
        ? { label: ex, text: ex, clamp: false }
        : { label: ex.label, text: ex.text, clamp: true }));
    },
    // Display the picked image as a small THUMBNAIL via a same-origin URL so the
    // page's img-src CSP doesn't block it. Prefers a thumbnail-sized source
    // (row.image.thumbnail) over the full image. data/blob and same-origin URLs load
    // directly; an external URL (e.g. a file-browser item whose baked-in host isn't
    // allowed) is proxied through the same-origin /share?file= endpoint. If it still
    // fails, @error shows a fallback icon instead of the broken-image glyph. (POR-13385)
    thumbSrc(row) {
      const raw = (row && row.image && (row.image.thumbnail || row.image.url)) || '';
      if (!raw) return '';
      if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
      const origin = (typeof window !== 'undefined' && window.location.origin) || '';
      if (!origin) return raw;
      // Add a downscale+WebP to any /share URL that doesn't already request one, so EVERY
      // source renders a tiny preview — including same-origin /share links that aren't
      // pre-lightened (e.g. It'sRapid stock's 512px thumbnail link). (POR-13385-4)
      const lighten = (u) => (u.indexOf('/share') === -1 || /[?&](lightweight|scale|is_thumbnail)=/.test(u)
        ? u
        : `${u}${u.indexOf('?') === -1 ? '?' : '&'}lightweight=1&scale=256&format=webp`);
      if (raw.startsWith('/')) return lighten(origin + raw);
      try {
        const parsed = new URL(raw);
        if (parsed.origin === origin) return lighten(raw);
        // External CDN image (Salsify / Stock Pixabay / Bynder): proxy through /share AND
        // ask for a downscaled WebP so the 30px preview isn't the full-size original (~MBs).
        // The full image used for generation lives on row.image.url, untouched. (POR-13385-4)
        return `${origin}/share?file=${encodeURIComponent(raw)}&lightweight=1&scale=256&format=webp`;
      } catch (e) {
        return raw;
      }
    },
    // The ORIGINAL full-resolution image (not the thumbnail) as a same-origin URL, exposed
    // via :data-fullsrc so the global lightbox handler (NetworksPageV2) opens the real image
    // instead of the 256px preview — the user needs to inspect it clearly to decide the A/B
    // variation. Mirrors thumbSrc's CSP handling but WITHOUT the downscale: data/blob and
    // same-origin URLs are used as-is; an external CDN url is proxied through /share with no
    // `lightweight`, which returns the original untouched. (POR-13385-7)
    fullSrc(row) {
      const raw = (row && row.image && row.image.url) || '';
      if (!raw) return '';
      if (raw.startsWith('data:') || raw.startsWith('blob:')) return raw;
      const origin = (typeof window !== 'undefined' && window.location.origin) || '';
      if (!origin) return raw;
      if (raw.startsWith('/')) return origin + raw;
      try {
        const parsed = new URL(raw);
        if (parsed.origin === origin) return raw;
        return `${origin}/share?file=${encodeURIComponent(raw)}`;
      } catch (e) {
        return raw;
      }
    },
    // Grow a textarea to fit ALL its content — no max-height/scroll — so a long example
    // shows in full with the action icons pinned to the top, matching the source design.
    // (POR-13564)
    autoGrow(el) {
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
    },
    // Re-measure EVERY row's textarea after the DOM updates and again after the next
    // paint, so none is left short with a stray scrollbar when content/layout settle
    // a tick late (the Variant A scrollbar). (POR-13385-3)
    growAll() {
      const grow = () => (this.$el.querySelectorAll('.abhero__text') || []).forEach((el) => this.autoGrow(el));
      grow();
      if (typeof requestAnimationFrame === 'function') requestAnimationFrame(grow);
    },
    onText(e) {
      this.autoGrow(e && e.target);
      this.emitState();
    },
    applyChip(idx, text) {
      this.$set(this.localRows, idx, { ...this.localRows[idx], text });
      this.$nextTick(() => this.growAll());
      this.emitState();
    },
    pick(idx, source) {
      this.$emit('action', `ab_hero_pick:${JSON.stringify({ rowKey: this.localRows[idx].key, source })}`);
    },
    // Open the Clipboard sidebar bound to THIS variant's editbox: the parent pre-selects
    // the Prompt tab, seeds "Save current" with the current text, and routes any prompt
    // the user picks back into this same box. (POR-13563-2)
    promptClipboard(idx) {
      const row = this.localRows[idx];
      if (!row) return;
      this.$emit('action', `ab_hero_prompt_clipboard:${JSON.stringify({
        kind: 'ab_picker',
        messageIndex: this.messageIndex,
        key: row.key,
        text: (row.text || ''),
      })}`);
    },
    // A prompt was picked in the Clipboard sidebar for this form → replace the target
    // row's text (mirrors the chat dock's replace-on-insert). Only the addressed
    // form/row reacts. (POR-13563-2)
    onInsertPrompt(p) {
      if (!p || p.kind !== 'ab_picker' || p.messageIndex !== this.messageIndex) return;
      const i = this.localRows.findIndex((r) => r.key === p.key);
      if (i === -1) return;
      this.$set(this.localRows, i, { ...this.localRows[i], text: String(p.text || '') });
      this.$nextTick(() => this.growAll());
      this.emitState();
    },
    onPaste(idx, e) {
      const items = (e.clipboardData && e.clipboardData.items) || [];
      for (let i = 0; i < items.length; i += 1) {
        if (items[i].type && items[i].type.indexOf('image') === 0) {
          const file = items[i].getAsFile();
          if (file) {
            e.preventDefault();
            // Keep the pasted image in THIS row only. The chat dock has a
            // document-level paste listener (onGlobalPaste) that otherwise also
            // attaches the same image to the chat composer — stop the event from
            // bubbling to it. (POR-13385)
            e.stopPropagation();
            // Parent uploads the blob and writes the URL back via imageResult.
            this.$emit('paste-image', { rowKey: this.localRows[idx].key, file });
            return;
          }
        }
      }
      // Text paste: the native @input handler runs autoGrow synchronously, but the
      // paste event fires BEFORE the browser inserts and wraps the text, so that
      // measurement lands a line short on a bulk insert and clips the box (e.g. 4 of
      // 5 lines). Re-measure after the DOM updates AND post-paint via growAll's
      // double-measure so the textarea fits the full pasted content. (POR-13385-6)
      this.$nextTick(() => this.growAll());
    },
    // Clear the variant's content (text and/or image), returning the row to its empty
    // state — which brings its example chips back, the same as deleting the text by hand.
    // (POR-13564)
    clearContent(idx) {
      this.$set(this.thumbErrors, this.localRows[idx].key, false);
      this.$set(this.localRows, idx, { ...this.localRows[idx], text: '', image: null });
      this.$nextTick(() => this.growAll());
      this.emitState();
    },
    // "Use original": this variant produces the SOURCE image unchanged (no AI) — the
    // A/B control against the AI variant. Drop the row's own text/image; the source is
    // resolved at generation time. (POR-13544)
    isOriginal(row) {
      return !!(row && row.mode === 'original');
    },
    useOriginal(idx) {
      this.sourceThumbError = false;
      this.$set(this.thumbErrors, this.localRows[idx].key, false);
      this.$set(this.localRows, idx, { ...this.localRows[idx], mode: 'original', text: '', image: null });
      this.emitState();
    },
    // Revert a "Use original" variant back to the normal AI editbox (empty + editable).
    clearOriginal(idx) {
      this.$set(this.localRows, idx, { ...this.localRows[idx], mode: null, text: '', image: null });
      this.$nextTick(() => this.growAll());
      this.emitState();
    },
    emitState() {
      this.$emit('action', `ab_hero_form_state:${JSON.stringify({ rows: this.localRows, size: this.localSize })}`);
    },
    generate() {
      if (!this.canGenerate) return;
      this.$emit('action', `ab_hero_generate:${JSON.stringify({ rows: this.localRows, size: this.localSize })}`);
    },
  },
};
</script>

<style scoped>
.abhero { background: transparent; padding: 0; }
.abhero__rowwrap { padding: 10px 0; border-bottom: 1px solid #e3e9e7; }
.abhero__rowwrap:last-child { border-bottom: none; }
/* Top-aligned: a long example expands the editbox fully (no scrollbar) with the label and
   the action icons pinned to the top, matching the source design. The label gets a small
   top padding so it lines up with the first line of text. (POR-13564) */
.abhero__row { display: flex; align-items: flex-start; gap: 12px; }
.abhero__label { width: 72px; flex: 0 0 72px; font-weight: 600; color: #51635d; font-size: 13px; padding-top: 7px; }
.abhero__input { flex: 1; display: flex; align-items: flex-start; gap: 6px; background: #fff; border: 1px solid #cdd6d3; border-radius: 8px; padding: 6px 10px; }
.abhero__input--disabled { opacity: .6; }
.abhero__thumb { width: 30px; height: 30px; object-fit: cover; border-radius: 6px; flex: 0 0 30px; border: 1px solid #e3e9e7; }
.abhero__thumb--fallback { display: inline-flex; align-items: center; justify-content: center; background: #eef2f1; color: #8a97a0; font-size: 15px; }
/* "Use original" chip: dashed-bordered source thumbnail + label, replacing the editbox. (POR-13544) */
.abhero__thumb--dashed { border: 1px dashed #b7c4bf; background: #f7f9f8; }
.abhero__originaltext { flex: 1; font-size: 13px; color: #51635d; }
.abhero__text { flex: 1; border: none; outline: none; font-size: 13px; background: transparent; resize: none; overflow: hidden; line-height: 1.4; min-height: 18px; font-family: inherit; padding: 0; display: block; }
/* Tight icon group at the right of the input, mirroring the chat composer's
   .ai-chat-input__icons (was spread by the input's 6px flex gap). (POR-13385-7) */
.abhero__icons { display: inline-flex; align-items: center; gap: 2px; flex: 0 0 auto; }
.abhero__icon { background: none; border: none; color: #8a97a0; cursor: pointer; padding: 2px; font-size: 15px; }
/* All three picker icons are Lucide glyphs inlined as SVG (Lovable/shadcn ship lucide-react), so they
   match the source design exactly instead of approximating with Bootstrap glyphs. (POR-13385-7) */
.abhero__icon-svg { display: block; }
/* The Lucide icons have near-equal ink widths, but FileImage (a portrait document) is the narrowest,
   leaving a hair more space before it; a 1px optical nudge keeps the gaps between all three even. */
.abhero__icon--useorig { margin-left: -1px; }
.abhero__icon:hover:not(:disabled) { color: #1f8a6d; }
/* The clear × now lives inside .abhero__icons (POR-13563-2); match the icon buttons'
   padding so all icons in the group are evenly spaced and vertically aligned. */
.abhero__clear { background: none; border: none; color: #8a97a0; cursor: pointer; font-size: 16px; line-height: 1; padding: 2px; }
.abhero__clear:hover:not(:disabled) { color: #1f8a6d; }
/* Sample-prompt boxes, aligned under the editbox (past the 72px label + 12px gap).
   One or more per variant; click fills the editbox with that example's full text. (POR-13564) */
.abhero__samplewrap { padding-left: 84px; margin-top: 8px; display: flex; flex-direction: column; gap: 8px; }
.abhero__sample { display: block; width: 100%; text-align: left; background: #f7f9f8; border: 1px dashed #b7c4bf; color: #51635d; border-radius: 8px; padding: 8px 11px; font-size: 12px; line-height: 1.4; cursor: pointer; font-family: inherit; }
/* A long example shows only its short label, clamped to 2 lines; the full text is still
   inserted on click and shown on hover via the title attribute. (POR-13564) */
.abhero__sample--clamp { display: -webkit-box; -webkit-box-orient: vertical; -webkit-line-clamp: 2; overflow: hidden; }
.abhero__sample:hover:not(:disabled) { border-color: #9aa5a1; color: #2b3a36; background: #eef2f1; }
.abhero__footer { display: flex; justify-content: space-between; align-items: center; margin-top: 12px; gap: 12px; }
.abhero__size { display: inline-flex; align-items: center; gap: 8px; font-size: 12px; color: #51635d; }
.abhero__size-label { font-weight: 600; }
.abhero__size-select { border: 1px solid #cdd6d3; border-radius: 8px; padding: 6px 8px; font-size: 13px; color: #2b3a36; background: #fff; cursor: pointer; }
.abhero__size-select:disabled { opacity: .6; cursor: default; }
.abhero__generate { background: #1f8a6d; border: none; color: #fff; border-radius: 8px; padding: 8px 18px; font-size: 14px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 6px; }
.abhero__generate:disabled { background: #a7c9bd; cursor: default; }
</style>
